import { useEffect, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import Footer from '../components/Footer';
import ButtonStyled from '../components/ButtonStyled';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { getUser } from '../services/userService';

const AccountPage = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [userData, setUserData] = useState({
        _id: '',
        email: '',
        address: '',
        colony: '',
        postalCode: '',
        region: '',
        city: '',
        references: ''
    });

    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const userId = localStorage.getItem('UID') || '';

    useEffect(() => {
        if (!userId) {
            navigate('/login');
            return;
        }

        const loadUser = async () => {
            setIsLoading(true);
            try {
                const response = await getUser(userId);
                const currentUser = response?.user || {};
                setUserData({
                    _id: currentUser?._id || userId,
                    email: currentUser?.email || '',
                    address: currentUser?.address || '',
                    colony: currentUser?.colony || '',
                    postalCode: currentUser?.postalCode || '',
                    region: currentUser?.region || '',
                    city: currentUser?.city || '',
                    references: currentUser?.references || ''
                });
            } catch (error) {
                console.error(error);
                enqueueSnackbar('No fue posible cargar la información de la cuenta.', {
                    variant: 'error'
                });
            } finally {
                setIsLoading(false);
            }
        };

        loadUser();
    }, [navigate, userId, enqueueSnackbar]);

    const getAddressText = () => {
        const fields = [
            userData.address,
            userData.colony,
            userData.city,
            userData.region,
            userData.postalCode ? `C.P. ${userData.postalCode}` : ''
        ].filter(Boolean);

        if (!fields.length) {
            return 'Aún no tienes una dirección de envío registrada.';
        }

        return fields.join(', ');
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: { xs: '1.5rem', md: '2rem' },
                    pt: {
                        xs: '6.1825rem',
                        sm: '6.5575rem',
                        md: '6.4975rem',
                        lg: '8.75rem'
                    },
                    px: { xs: '1.5625rem', md: '3.12rem', lg: '4.5rem', xl: '6.25rem' },
                    pb: '5rem'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton
                        sx={{ padding: 0 }}
                        onClick={() => {
                            navigate(-1);
                        }}
                    >
                        <NavigateNextIcon
                            sx={{
                                transform: 'rotate(180deg)',
                                ml: { xs: '0px' },
                                fontSize: { xs: '1.5rem', md: '2rem' },
                                color: '#FC581F'
                            }}
                        />
                    </IconButton>
                    <Typography
                        sx={{
                            color: '#303030',
                            fontSize: { xs: '1.25rem', md: '1.5rem' },
                            fontWeight: 'bold'
                        }}
                    >
                        Mi cuenta
                    </Typography>
                </Box>

                <Box
                    sx={{
                        border: '1px solid #E6E6E6',
                        borderRadius: '0.5rem',
                        p: { xs: '1rem', md: '1.5rem' },
                        boxShadow: '1px 1px 4px 0px rgba(0, 0, 0, 0.08)'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem', pb: '1rem' }}>
                        <LockResetOutlinedIcon sx={{ color: '#FC581F' }} />
                        <Typography sx={{ fontWeight: 'bold', color: '#303030', fontSize: { xs: '1rem', md: '1.125rem' } }}>
                            Seguridad
                        </Typography>
                    </Box>

                    <Typography sx={{ color: '#575757', fontSize: { xs: '0.875rem', md: '1rem' }, pb: '0.75rem' }}>
                        Administra la contraseña de tu cuenta.
                    </Typography>

                    <Box
                        onClick={() => navigate('/account/change-password')}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: '0.75rem',
                            py: '0.875rem',
                            border: '1px solid #F0F0F0',
                            borderRadius: '0.25rem',
                            cursor: 'pointer',
                            '&:hover': {
                                borderColor: '#FC581F'
                            }
                        }}
                    >
                        <Typography sx={{ color: '#303030', fontSize: { xs: '0.875rem', md: '1rem' }, fontWeight: 'bold' }}>
                            Cambiar contraseña
                        </Typography>
                        <NavigateNextIcon sx={{ color: '#FC581F' }} />
                    </Box>
                </Box>

                <Box
                    sx={{
                        border: '1px solid #E6E6E6',
                        borderRadius: '0.5rem',
                        p: { xs: '1rem', md: '1.5rem' },
                        boxShadow: '1px 1px 4px 0px rgba(0, 0, 0, 0.08)'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem', pb: '1rem' }}>
                        <LocalShippingOutlinedIcon sx={{ color: '#FC581F' }} />
                        <Typography sx={{ fontWeight: 'bold', color: '#303030', fontSize: { xs: '1rem', md: '1.125rem' } }}>
                            Dirección de envío
                        </Typography>
                    </Box>

                    <Typography sx={{ color: '#575757', fontSize: { xs: '0.875rem', md: '1rem' }, pb: '1rem' }}>
                        {getAddressText()}
                    </Typography>

                    <ButtonStyled
                        text={isLoading ? 'Cargando...' : 'Actualizar dirección'}
                        isDisabled={isLoading}
                        handleClick={() => navigate('/user/' + userId)}
                        customStyle={{ width: { xs: '100%', md: 'auto' } }}
                    />
                </Box>
            </Box>
            <Footer />
        </Box>
    );
};

export default AccountPage;
